import { useMemo, CSSProperties, Suspense } from 'react';
import { Translate, useGetList } from 'react-admin';
import {
    useMediaQuery,
    Theme,
    Skeleton,
    Card,
    CardHeader,
    CardContent,
} from '@mui/material';
import { subDays, startOfDay } from 'date-fns';

import Welcome from '@/pages/dashboard/Welcome';
import MonthlyRevenue from '@/pages/dashboard/MonthlyRevenue';
import NbNewOrders from '@/pages/dashboard/NbNewOrders';

interface OrderStats {
    revenue: number;
    nbNewOrders: number;
}

interface State {
    nbNewOrders?: number;
    recentOrders?: any[];
    revenue?: string;
}

const styles = {
    flex: { display: 'flex' },
    flexColumn: { display: 'flex', flexDirection: 'column' },
    leftCol: { flex: 1, marginRight: '0.5em' },
    rightCol: { flex: 1, marginLeft: '0.5em' },
    singleCol: { marginTop: '1em', marginBottom: '1em' },
};

const Spacer = () => <span style={{ width: '1em' }} />;
const VerticalSpacer = () => <span style={{ height: '1em' }} />;

const Dashboard = () => {
    const isXSmall = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('sm')
    );
    const isSmall = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('lg')
    );
    const aMonthAgo = useMemo(() => subDays(startOfDay(new Date()), 30), []);

    const { data: orders } = useGetList<any>('orders', {
        filter: { date_gte: aMonthAgo.toISOString() },
        sort: { field: 'date', order: 'DESC' },
        pagination: { page: 1, perPage: 50 },
    });

    const aggregation = useMemo<State>(() => {
        if (!orders) return {};
        const aggregations = orders
            .filter(order => order.status !== 'cancelled')
            .reduce(
                (stats: OrderStats, order) => {
                    if (order.status !== 'cancelled') {
                        stats.revenue += order.total;
                        stats.nbNewOrders++;
                    }
                    return stats;
                },
                {
                    revenue: 0,
                    nbNewOrders: 0,
                }
            );
        return {
            recentOrders: orders,
            revenue: aggregations.revenue.toLocaleString(undefined, {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }),
            nbNewOrders: aggregations.nbNewOrders,
        };
    }, [orders]);

    const { nbNewOrders, revenue, recentOrders } = aggregation;
    return isXSmall ? (
        <div>
            <div style={styles.flexColumn as CSSProperties}>
                <Welcome />
                <MonthlyRevenue value={revenue} />
                <VerticalSpacer />
                <NbNewOrders value={nbNewOrders} />
                <VerticalSpacer />
            </div>
        </div>
    ) : isSmall ? (
        <div style={styles.flexColumn as CSSProperties}>
            <div style={styles.singleCol}>
                <Welcome />
            </div>
            <div style={styles.flex}>
                <MonthlyRevenue value={revenue} />
                <Spacer />
                <NbNewOrders value={nbNewOrders} />
            </div>
            <div style={styles.singleCol}>
                <Card>
                    <CardHeader
                        title={
                            <Translate i18nKey="pos.dashboard.month_history" />
                        }
                    />
                    <CardContent>
                        <Suspense fallback={<Skeleton height={300} />}>
                        </Suspense>
                    </CardContent>
                </Card>
            </div>
            <div style={styles.singleCol}>
            </div>
        </div>
    ) : (
        <>
            <Welcome />
            <div style={styles.flex}>
                <div style={styles.leftCol}>
                    <div style={styles.flex}>
                        <MonthlyRevenue value={revenue} />
                        <Spacer />
                        <NbNewOrders value={nbNewOrders} />
                    </div>
                    <div style={styles.singleCol}>
                        <Card>
                            <CardHeader
                                title={
                                    <Translate i18nKey="pos.dashboard.month_history" />
                                }
                            />
                            <CardContent>
                                <Suspense fallback={<Skeleton height={300} />}>
                                </Suspense>
                            </CardContent>
                        </Card>
                    </div>
                    <div style={styles.singleCol}>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Dashboard;
